import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  Columns,
  Column,
  IconHeart,
  IconHistory,
  Stack,
  Text,
  TextLink,
  IconArrow,
  Heading,
  Link,
} from 'braid-design-system';

import { useSelector } from 'src/store/react';
import { selectAuthenticated } from 'src/store/selectors';

import translations from './.vocab';

import * as styles from './SavedRecentSearches.css';

const MAX_SAVED_SEARCHES = 3;
const MAX_RECENT_SEARCHES = 3;

interface SearchItemProps {
  label: string;
  count?: string;
  summary?: string;
}

const renderSearchItem = (
  searchItem: SearchItemProps,
  icon: JSX.Element,
  key: string,
) => {
  const { label, count, summary } = searchItem;
  return (
    <Link key={key} href="">
      <Box
        width="full"
        className={{ [styles.searchItem]: true, [styles.menuItem]: true }}
      >
        <Columns space="small" alignY="center">
          <Column width="content" hideBelow="wide">
            <Heading level="4" component="span">
              <span className={styles.searchItemIcon}>{icon}</span>
            </Heading>
          </Column>
          <Column>
            <Stack space="xxsmall">
              <Text
                size="standard"
                tone="neutral"
                baseline={false}
                maxLines={summary ? 1 : 2}
              >
                {label}
              </Text>
              {summary ? (
                <Text size="standard" tone="secondary" maxLines={1}>
                  {summary}
                </Text>
              ) : null}
            </Stack>
          </Column>
          <Column width="content">
            {count && (
              <Badge tone="positive" bleedY={true}>
                {count}
              </Badge>
            )}
          </Column>
        </Columns>
      </Box>
    </Link>
  );
};

export const SavedRecentSearches = () => {
  // TODO Mocking these for now
  const recentSearches = [
    { label: 'accounting in Hong Kong' },
    { label: 'All jobs in Australia', count: '8 new' },
    {
      label: 'I want a job that allows me to work with an amazing designer',
      count: '8 new',
    },
  ];
  const savedSearches = [
    { label: 'All jobs in Australia' },
    {
      label: 'Product Owner in finance',
      count: '8 new',
      summary: '$50 to $350+ Annually, Full time',
    },
    {
      label: 'UX/UI Designer in Australia',
      count: '99+ new',
      summary: '$50 to $350+ Annually, Full time',
    },
    {
      label: 'Accounting',
      count: '99+ new',
      summary: '$50 to $350+ Annually, Full time',
    },
    {
      label: 'Accounting',
      count: '99+ new',
      summary: '$50 to $350+ Annually, Full time',
    },
  ];

  const { t } = useTranslations(translations);

  const totalSavedSearches = savedSearches.length;
  const extraSavedSearches = totalSavedSearches > MAX_SAVED_SEARCHES;
  const savedSearchesToRender = savedSearches.slice(0, MAX_SAVED_SEARCHES);

  const authenticated = useSelector(selectAuthenticated);
  const renderSavedSearches = authenticated && savedSearchesToRender.length;

  const recentSearchesToRender = recentSearches.slice(0, MAX_RECENT_SEARCHES);

  if (!recentSearches.length && !savedSearches.length) {
    return;
  }

  return (
    <Box
      id="SavedRecentSearches"
      background="surface"
      className={styles.savedRecentPanel}
      boxShadow="large"
      paddingBottom="xsmall"
      paddingTop="small"
      tabIndex={-1}
    >
      {recentSearches.length && (
        <Box
          className="recentSearches"
          paddingBottom={renderSavedSearches ? 'xxsmall' : 'none'}
        >
          <Box className={styles.menuItem}>
            <Text weight="strong" size="small" tone="secondary">
              {t('Recent searches')}
            </Text>
          </Box>
          <Box className="searchItemSection">
            {recentSearchesToRender.map((recentSearch, index) =>
              renderSearchItem(
                recentSearch,
                <IconHistory />,
                `recentSearch-${index}`,
              ),
            )}
          </Box>
        </Box>
      )}

      {renderSavedSearches && (
        <Box className="savedSearches">
          <Box className={styles.menuItem}>
            <Text weight="strong" size="small" tone="secondary">
              {t('Saved searches')}
            </Text>
          </Box>
          <Box className="searchItemSection">
            {savedSearchesToRender.map((savedSearch, index) =>
              renderSearchItem(
                savedSearch,
                <IconHeart />,
                `savedSearch-${index}`,
              ),
            )}
          </Box>
          {extraSavedSearches ? (
            <Box className={styles.menuItem}>
              <Text size="small">
                <TextLink
                  href="/my-activity/saved-searches"
                  icon={<IconArrow direction="right" />}
                  iconPosition="trailing"
                  weight="weak"
                >
                  {t('See all ({count})', {
                    count: totalSavedSearches.toString(),
                  })}
                </TextLink>
              </Text>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
};
